import { useEffect, useRef, useState } from 'react';

/**
 * Whether a `line-clamp-*` element is hiding some of its text.
 *
 * CSS draws the ellipsis but cannot say it did, and a cut-off notice needs a
 * way to the rest of it — so the element is measured: clamped text is taller
 * than the box it is clipped to. Re-measured as the box resizes, because the
 * same notice is three lines on a phone and one on a wide screen.
 *
 * `deps` re-measures when the text itself changes.
 */
export function useIsClamped<T extends HTMLElement>(deps: readonly unknown[] = []) {
  const ref = useRef<T>(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // One pixel of slack: sub-pixel line heights round differently by browser.
    const measure = () => setIsClamped(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
    // The caller's deps are the trigger; the ref is stable.
  }, deps);

  return { ref, isClamped };
}
